import { Component, DoCheck, HostListener, Inject, OnDestroy, OnInit, DOCUMENT } from "@angular/core";
import { ManagedContentService } from "./shared/service/managed-content.service";
import { RequestState } from "./shared/model/request-state.enum";
import { CmHelp, ContentType } from "./shared/model/cm/managed-content.model";
import { UserSessionService } from "./shared/service/user-session.service";
import { EnvService } from "./env-service/env.service";
import { AppInsightsLoggerService } from "./shared/service/app-insights-logger.service";
import { Location } from "@angular/common";
import { COMPANY_ID, COMPANY_DATA_THEME, GlobalConstants } from "./shared/global-constants";
import { swap } from 'favicon-swapper';
import { Router, RouterOutlet } from "@angular/router";

import { PagetitleService } from "./shared/service/pagetitle.service";
import { AuthGuardService } from "./shared/service/auth-guard.service";
import '@prism/prism-ui-components/dist/esm/prism-ui-components.js';
import { ModalService } from "./shared/service/modal.service";
import { WindowRef } from "./shared/service/window.service";
import { FormUtils } from "./shared/helper/form-utils";
import { GoogleAnalyticsService } from "./shared/service/google-analytics.service";
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { NavbarComponent } from "./navbar/navbar.component";
import { ReactiveFormsModule } from "@angular/forms";
import { AmuTimerService } from "./shared/service/amu-timer.service";
import { Subscription } from "rxjs";

export let browserRefresh = false;

@Component({
    selector: "app-root",
    standalone: true,
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    providers: [],
    imports: [RouterOutlet, NavbarComponent, ReactiveFormsModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppComponent implements OnInit, DoCheck, OnDestroy {
  contactUsContent: CmHelp;

  private cancelContent: CmHelp;
  public modalContentHeading: string;
  public modalContentBody: string;
  public timerContentHeading: string = "Are you still there?";
  public timerContentBody: string = "For your security, we will end your session and clear your data after an extended period of inactivity. Your session will automatically end soon.";

  public legalURL: string = "";
  public companyName: string = "";
  public disclaimer: string = "";
  public isStandardTheme: boolean = true;
  public isInstructionsPage: boolean = false;

  title = "angular-eeoi";
  public cmInitialized: boolean;

  private idleSubscription?: Subscription;

  constructor(
    private cmService: ManagedContentService,
    private userSessionService: UserSessionService,
    private envService: EnvService,
    private appInsightsLoggerService: AppInsightsLoggerService,
    private router: Router,
    private location: Location,
    private googleAnalyticsService: GoogleAnalyticsService,
    @Inject(DOCUMENT) private document: Document,
    private pagetitleService: PagetitleService,
    private authGuardService: AuthGuardService,
    private modalService: ModalService,
    private windowService: WindowRef, 
    private timerService: AmuTimerService
  ) {
    let currentPage = window.location.pathname; // current page
    // these pages should have consent to view. can not refresh
    let dataRequiredPages = [
      "/state-of-residence",
      "/policy",
      "/applicant-type",
      "/coverage",
      "/mhs",
      "/confirmation",
      "/esign-disclosure",
      "/employee-information",
      "/applicant-information",
      "/medical-history",
      "/physician-information",
      "/authorization",
      "/review",
      "/review"
    ];

    if (
      !!currentPage &&
      currentPage !== "/" &&
      currentPage.indexOf(".") == -1 && // not .html or the like
      userSessionService.data.consent !== true
    ) {
      let lastPageIndex = currentPage.lastIndexOf("/");
      if (lastPageIndex > -1) {
        let matchCurrentPage = currentPage.substring(lastPageIndex);
        if (dataRequiredPages.indexOf(matchCurrentPage) != -1) {
          this.appInsightsLoggerService.log(
            "error",
            "MISSING NECESSARY DATA on ",
            currentPage
          );
          // should probably display a user friendy message to explain why this is happening
          window.location.href = this.envService.deployPath + "/";
        }
      }
    }
  }

  ngOnInit() {
    this.googleAnalyticsService.initializeVirtualPageViewTracking();
    this.listenForManagedContentRequest();
    this.setConfigBasedOnDomainUsed();
    this.setFavicon();
    this.setFooter();

    this.cancelContent = this.cmService.getContentById(ContentType.HELP, "lose-data-warning");

    this.modalContentHeading = this.cancelContent.title;
    this.modalContentBody = FormUtils.sanitize(this.cancelContent.body);

    // Check current route and subscribe to route changes
    this.checkIfInstructionsPage();
    this.router.events.subscribe(() => {
      this.checkIfInstructionsPage();
    });

    this.timerService.isTimerStarted.subscribe((isStarted) => {
      if(isStarted){
        this.timerService.getIdleStatus.subscribe((isIdle) => {
          if (isIdle === "showWarning") {
            this.showTimeoutWarning();
          } else if (isIdle === "timeOut") {
            this.EndSession();
          } else if (isIdle === "resetTimer") {
            
          } 
        });
      } 
    });

    //modify company name in the footer if the company changes to YI
    if(this.pagetitleService){
      this.pagetitleService.isYiPolicy.subscribe(isYi => {
        if(isYi){
          this.companyName = GlobalConstants.CARRIER_NAME_SNY;
        }else{
          if (this.userSessionService.data.dataTheme === COMPANY_DATA_THEME.NM){
            this.companyName = GlobalConstants.CARRIER_NAME_NM;
          } else {
            this.companyName = GlobalConstants.CARRIER_NAME_SFG;
          }
        }
      });
    }

    this.windowService.nativeDocument
      .querySelector('body')
      .addEventListener('click', (event: Event) => this.onClick(event));
  }

  ngOnDestroy(): void {
    if(this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }
  }

  onUserAction(){
    this.timerService.resetTimer();
  }

  ngDoCheck() {

    if(this.userSessionService.data.edpGroupData && (this.userSessionService.data.edpGroupData.companyId=='YI' && this.userSessionService.data.edpGroupData.state=='NY')){
      this.companyName = GlobalConstants.CARRIER_NAME_SNY;
    }
  }

  showTimeoutWarning(): void {
    this.appInsightsLoggerService.log('info', 'Info log from app.component', `User is idle due to inactivity.`);
    this.timerService.showTimeoutWarning();
  }

  //This code is used to handle the back button on the browser
  //it makes the page title have the correct title
  @HostListener('window:popstate', ['$event'])
  onPopState(event) {
    const pageName = this.getPageName(this.router.url);
    
    // Check if we're on an MHS page - let MHS component handle browser back button
    const mhsPages = ['employee-information', 'applicant-information', 'medical-history', 'physician-information', 'authorization', 'electronic-communication'];
    const isOnMhsPage = mhsPages.some(page => this.router.url.includes(page));
    
    if(this.router.url != '/confirmation' && !isOnMhsPage){
      if (pageName) {
        this.pagetitleService.setPageTitle(pageName);
      }

      this.modalContentHeading = this.cancelContent.title;
      this.modalContentBody = FormUtils.sanitize(this.cancelContent.body);

      this.modalService.showModal('prism-ui-modal-cancel-modal'); // Use the service to show the modal
    }
  }

  private getPageName(url: string): string {
    switch (url) {
      case '/policy':
        return 'Policy';
      case '/consent':
        return 'Consent';
      case '/state-of-residence':
        return 'State of Residence';
      case '/applicant-type':
        return 'Applicant Type';
      case '/coverage':
        return 'Coverage';
      case '/mhs':
        return 'Medical History';
      case '/esign-disclosure':
        return 'E-Signature Disclosure';
      case '/invalid-policy-number':
        return 'Invalid Policy Number';
      case '/page-not-found':
        return 'Page Not Found';
      case '/review':
        return 'Review';
      case '/instructions':
        return 'Instructions';
      case '/confirmation':
        return 'Confirmation';
      default:
        return '';
    }
  }

  private listenForManagedContentRequest(): void {
    this.cmService.loadManagedContent().subscribe((next) => {
      if (next === RequestState.PRISTINE || next === RequestState.WAITING) {
        this.cmInitialized = false;
      } else if (next === RequestState.SUCCESS) {
        this.cmInitialized = true;
        //RxJsHelper.unSubscribeSafely(this.subscription);
      } else if (next === RequestState.FAIL) {
        //this.router.navigate([CIW_APP_ROUTES.SOMETHING_WENT_WRONG]);
        this.cmInitialized = true;
        //RxJsHelper.unSubscribeSafely(this.subscription);
      }
    });
  }

  endSession():void {
    this.userSessionService.data.sessionEnded = true;
    this.timerService.startOrStopTimer(false);
    this.authGuardService.cancelLeaveAnyway();
    this.router.navigate(["instructions"]);
  }

  keepWorking(){
    this.appInsightsLoggerService.log('info', 'Info log from app.component', `User chose to keep working.`);
    this.timerService.resetTimer();
    this.modalService.closeModal('prism-ui-modal-timer-modal');
  }

  EndSession(){ 
    this.appInsightsLoggerService.log('info', 'Info log from app.component', `User chose to end session due to inactivity.`);
    this.endSession();
    this.modalService.closeModal('prism-ui-modal-timer-modal');
  }

  onCancelModal(modalName: string){
    const modalElement =
      this.windowService.nativeDocument.getElementById(modalName);

    if (modalElement && modalElement.close) {
      modalElement.close();
    } else {
      this.appInsightsLoggerService.log('info', 'Info log from app.component', `Modal with id ${modalName} not found or doesn't support close method.`);
    }
  }

  onErrorModal(modalName: string){
    const modalElement =
      this.windowService.nativeDocument.getElementById(modalName);

    if (modalElement && modalElement.close) {
      modalElement.close();
    } else {
      this.appInsightsLoggerService.log('info', 'Info log from app.component', `Modal with id ${modalName} not found or doesn't support close method.`);
    }
  }

  // use the window service to access the modal element
  // and close the modal when the user clicks outside of it
  onClick(event: Event) {
    const modal = this.windowService.nativeDocument.getElementById(
      'prism-ui-modal-error-modal'
    );

    if (modal && event.target === modal) {
      this.onErrorModal('prism-ui-modal-error-modal');
    }
  }

  private checkIfInstructionsPage(): void {
    const currentUrl = this.router.url;
    // Check if URL is /instructions, /instructions/:timeout, /:policyId (6-digit number)
    // Also handles deploy path patterns like /voy5606/ui/instructions
    const isInstructionsRoute = currentUrl === '/instructions' || 
                                 currentUrl.startsWith('/instructions/') ||
                                 currentUrl.includes('/instructions');
    const isPolicyIdRoute = /^\/\d{6}$/.test(currentUrl);
    this.isInstructionsPage = isInstructionsRoute || isPolicyIdRoute;
  }

  leaveAnyway(){
    this.modalService.closeModal('prism-ui-modal-cancel-modal');
    this.userSessionService.clearSession();
    this.userSessionService.data.sessionEnded = true;
    this.authGuardService.cancelLeaveAnyway();
    this.router.navigate(['/instructions']);
  }

  //This code is used to handle the refresh button on the browser
  @HostListener("window:beforeunload", ["$event"])
  async beforeunloadHandler($event) {
    if (
      !this.userSessionService.data.sessionEnded
    ) {
      $event.preventDefault();
      $event.returnValue = "";
    }
  }

  /**
   * This method is used to set the Backend APIs paths that needs to be used based on which domain
   * the angular frontend is accessed from.
   * This method also sets the dataTheme used by prism based on the domain we are accessing the angular from.
   */
  private setConfigBasedOnDomainUsed() {
    if (
        (this.envService.nmDomainName !== "" && this.envService.nmDomainName === window.location.hostname)
        ||
        window.location.href.startsWith("http://localhost:4201")
      )
        {
      this.userSessionService.data.dataTheme = COMPANY_DATA_THEME.NM;
      this.userSessionService.data.companyId = COMPANY_ID.NM;
      this.envService.contractsApiEndpoint = this.envService.alternateContractsApiEndpoint;
      this.envService.mhsApiEndpoint = this.envService.alternateMhsApiEndpoint;
    } else {
      this.userSessionService.data.dataTheme = COMPANY_DATA_THEME.SFG;
      this.userSessionService.data.companyId = COMPANY_ID.SI;
    }
    // Set the attribute used by prism
    const body = document.getElementsByTagName('body')[0];
    body.setAttribute("data-theme", this.userSessionService.data.dataTheme);
  }

  private setFavicon() {
    if (this.userSessionService.data.dataTheme === COMPANY_DATA_THEME.NM) {
      swap('./assets/images/nmColumnCapLogo.png');
    } else {
      swap('./assets/images/standardLogo.svg');
      //this logo isn't working for some reason (it used to)
      //swap('./assets/images/standardLogoAllBlue.svg');
    }
  }

  private setFooter(){
    if (this.userSessionService.data.dataTheme === COMPANY_DATA_THEME.NM){
      this.legalURL = GlobalConstants.LEGAL_URL_NM;
      this.companyName = GlobalConstants.CARRIER_NAME_NM;
      this.disclaimer = "";
      this.isStandardTheme = false;
    } else {
      this.legalURL = GlobalConstants.LEGAL_URL_SFG;
      this.companyName = GlobalConstants.CARRIER_NAME_SFG;
      this.disclaimer = "";
      this.isStandardTheme = true;
    }
  }
}
